'use client';

import {useState} from 'react';
import {m as motion} from 'motion/react';
import type {Map as MapboxMap} from 'mapbox-gl';
import ReactCountryFlag from 'react-country-flag';
import type {VisitedStats} from 'server/apiHandlers/getVisitedStatsHandler';
import {ratingStar, skipDownLine, skipUpLine} from 'src/utils/icons';
import {setSearchParam} from 'src/hooks/useSearchParam';
import MapPlaceListsStats from './MapPlaceListsStats';
import {tabContentMotionProps} from '../common/Tabs';
import getCountriesStats from './getCountriesStats';
import {filterPlaces} from './useFilteredPlaces';
import roundNumber from 'src/utils/roundNumber';
import pluralize from 'src/utils/pluralize';
import useMapUtils from './useMapUtils';
import BaseButton from '../BaseButton';
import RemixIcon from '../RemixIcon';

type Props = {
  list: HumboList;
  reviews: Review[];
  visitedStats: VisitedStats;
  map: MapboxMap | null;
};

const MapCountriesStats = ({list, reviews, visitedStats, map}: Props) => {
  const [showAllCountries, setShowAllCountries] = useState(false);
  const {zoomToPlaces} = useMapUtils(map);

  const countriesStats = getCountriesStats(list, reviews);

  const numberOfCountries = Object.keys(countriesStats.countriesStats).length;
  const topVisitedCountries = Object.values(countriesStats.countriesStats).sort((a, b) => b.count - a.count);

  return (
    <motion.div
      className="w-full pb-8 origin-[center_100px] overflow-auto max-[800px]:overflow-visible py-1 px-px pr-6 max-[800px]:pr-4"
      {...tabContentMotionProps}
    >
      <div className="bg-(--green-secondary) rounded-3xl p-3 w-full">
        <h2 className="pt-1 pb-3 px-0 font-semibold text-(--green-primary) dark:text-(--green-primary)">
          <span className="text-4xl max-[800px]:text-2xl mr-1">{numberOfCountries}</span>{' '}
          {pluralize('Country', numberOfCountries, 'Countries')} visited
        </h2>

        <ul className="flex flex-col gap-3">
          {topVisitedCountries.slice(0, showAllCountries ? topVisitedCountries.length : 5).map((countryStats) => {
            const percentage = (countryStats.count / topVisitedCountries[0]?.count) * 100;

            return (
              <li key={countryStats.countryCode} className="w-full h-10">
                <BaseButton
                  onPress={() => {
                    setSearchParam('country', countryStats.countryCode);
                    setSearchParam('tab', 'places');
                    zoomToPlaces(
                      filterPlaces(
                        list.places.map((placeEntry) => placeEntry.place),
                        '',
                        countryStats.countryCode
                      )
                    );
                  }}
                  className="p-0 w-full justify-between text-(--green-primary) px-3 relative duration-0"
                  buttonVariant="text"
                >
                  <div
                    className="absolute bg-[#b4f7bb] dark:bg-[#223d2b] w-1/2 rounded-full h-10 left-0"
                    style={{width: `${percentage}%`}}
                  />

                  <div className="font-semibold flex items-center gap-2 relative grow w-px">
                    <ReactCountryFlag
                      countryCode={countryStats.countryCode}
                      style={{width: 24, height: 'auto'}}
                      className="rounded-sm shadow-[0px_1px_2px_rgba(128,115,133,0.08),0px_3px_7px_rgba(128,119,133,0.1)]"
                      svg
                    />
                    <span className="truncate">{countryStats.name}</span>
                  </div>

                  <div className="relative flex items-center gap-1 font-semibold shrink-0">
                    <span>
                      {countryStats.count} {pluralize('place', countryStats.count)}{' '}
                      <span className="opacity-30">|</span> {roundNumber(countryStats.averageRating)}
                    </span>
                    <RemixIcon icon={ratingStar} size={14} color="var(--green-primary)" />
                  </div>
                </BaseButton>
              </li>
            );
          })}
        </ul>

        {topVisitedCountries.length > 5 ? (
          <div className="flex justify-center mt-2">
            <BaseButton
              color="var(--green-primary)"
              onPress={() => setShowAllCountries(!showAllCountries)}
              label={showAllCountries ? 'Show less' : 'Show more'}
              icon={showAllCountries ? skipUpLine : skipDownLine}
              buttonVariant="text"
              size="small"
            />
          </div>
        ) : null}
      </div>

      <MapPlaceListsStats visitedStats={visitedStats} />
    </motion.div>
  );
};

export default MapCountriesStats;
